import { Box, IconButton, Typography } from "@mui/material";
import GooglePlacesAutocomplete, {
  geocodeByPlaceId,
} from "react-google-places-autocomplete";
import React, { useEffect, useMemo, useState } from "react";
import { LocationOn } from "@mui/icons-material";
import { MapPicker } from "../../views/Chatbot/components/MapPicker";
import { IAddressDetails } from "../../views/Chatbot/components/MapPicker";
import { config } from "../../config";

export interface ILocationResponsePayload {
  place: string
  country: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  postal_code: string;
  region: string;
  fullAddress?: string;
}

interface ILocationAutoCompleteProps {
  onLocationChange: (data: ILocationResponsePayload) => void;
  id?: string | number;
  value: string | number | null;
  [key: string]: any;
  error?: boolean;
  helperText?: string;
}

export const LocationAutoCompleteWithManualAddress = (
  props: ILocationAutoCompleteProps,
) => {
  const [inputValue, setInputValue] = React.useState(
    props.value?.toString() || "",
  );
  const [mapPickerOpen, setMapPickerOpen] = React.useState(false);

  const selectedOption = useMemo(() => {
    if (!props.value) return null;
    const label = props.value.toString();
    return { label, value: label };
  }, [props.value]);

  useEffect(() => {
    setInputValue(props.value?.toString() || "");
  }, [props.value]);

  // Handle location selection from MapPicker
  const handleMapPickerLocationSelect = async (
    place: any,
    addressDetails?: IAddressDetails,
  ) => {
     console.log("handleMapPickerLocationSelect ==> ", addressDetails)
    if (addressDetails) {
      // If we have structured address details from MapPicker
      const formattedAddress = addressDetails.address || "";
      setInputValue(formattedAddress);

      // Call the parent's onLocationChange with the structured data
      props.onLocationChange({
        place:  addressDetails.place || "",
        country: addressDetails.country || "",
        address_line1:
          addressDetails.address_line1 || addressDetails.address || "",
        address_line2: addressDetails.address_line2 || "",
        city: addressDetails.city || "",
        state: addressDetails.state || "",
        postal_code: addressDetails.pin_code || "",
        region: addressDetails.region || "IND",
        fullAddress: formattedAddress,
      });
    } else if (place?.value?.place_id) {
      // If we have a place with place_id, use geocoding to get details
      try {
        const response = await geocodeByPlaceId(place.value.place_id);
        const { address_components, formatted_address } = response[0];

        // Parse address components (similar to your existing logic)
        let placeName = place?.value?.structured_formatting?.main_text ?? "";
        let city = "";
        let state = "";
        let country = "";
        let zipCode = "";
        let addressLine1 = "";
        let addressLine2 = "";

        for (const component of address_components) {
          const types = component.types;
          if (types.includes("locality")) {
            city = component.long_name;
          } else if (types.includes("administrative_area_level_1")) {
            state = component.long_name;
          } else if (types.includes("country")) {
            country = component.long_name;
          } else if (types.includes("postal_code")) {
            zipCode = component.long_name;
          } else if (
            types.includes("street_number") ||
            types.includes("route") ||
            types.includes("premise")
          ) {
            addressLine1 += component.long_name + " ";
          } else if (
            types.includes("neighborhood") ||
            types.includes("sublocality") ||
            types.includes("sublocality_level_1") ||
            types.includes("sublocality_level_2")
          ) {
            addressLine2 += component.long_name + " ";
          }
        }

        const fullAddress = formatted_address || place.label || "";
        setInputValue(fullAddress);

        props.onLocationChange({
          place: placeName,
          country: country,
          address_line1: addressLine1.trim(),
          address_line2: addressLine2.trim(),
          city: city,
          state: state,
          postal_code: zipCode,
          region: "IND",
          fullAddress: fullAddress,
        });
      } catch (error) {
        console.error("Error fetching geocode data:", error);
        // Fallback to place label
        const fullAddress = place.label || "";
        setInputValue(fullAddress);
        props.onLocationChange({
          place: "",
          country: "",
          address_line1: fullAddress,
          address_line2: "",
          city: "",
          state: "",
          postal_code: "",
          region: "IND",
          fullAddress: fullAddress,
        });
      }
    }

    setMapPickerOpen(false);
  };

  const handleManualAddress = (manualAddress: string) => {
    setInputValue(manualAddress);
    props.onLocationChange({
      place: "",
      country: "",
      address_line1: "",
      address_line2: "",
      city: "",
      state: "",
      postal_code: "",
      region: "",
      fullAddress: manualAddress,
    });
  };

  return (
    <Box position="relative">
      <GooglePlacesAutocomplete
        apiKey={config.integration.googleMapsApiKey}
        key={props.value}
        selectProps={{
          innerProps: {
            id: props.id,
          },
          placeholder: "Choose Location or Type Your Address",
          isClearable: true,
          defaultInputValue: props.value?.toString(),
          value: selectedOption,
          inputValue: inputValue,

          menuPortalTarget: document.body,
          styles: {
            menuPortal: (base: any) => ({
              ...base,
              zIndex: 1300,
            }),
            menu: (base: any) => ({
              ...base,
              zIndex: 1300,
            }),
          },

          onInputChange: (value: string) => {
            setInputValue(value);
          },
          onChange: async (place: any) => {
            const placeId = place?.value?.place_id;

            if (!placeId) {
              if (!place) {
                setInputValue("");
                props.onLocationChange({
                  place: '',
                  country: "",
                  address_line1: "",
                  address_line2: "",
                  city: "",
                  state: "",
                  postal_code: "",
                  region: "",
                  fullAddress: "",
                });
                return;
              }
              if (inputValue && inputValue.trim()) {
                handleManualAddress(inputValue.trim());
              }
              return;
            }

            try {
              const response = await geocodeByPlaceId(placeId);
              const { address_components } = response[0];

              let placeName = place?.value?.structured_formatting?.main_text ?? "";
              let fullAddress = "";
              let city = "";
              let state = "";
              let country = "";
              let zipCode = "";
              let addressLine1 = "";
              let addressLine2 = "";

              for (const component of address_components) {
                const types = component.types;
                if (types.includes("locality")) {
                  city = component.long_name;
                } else if (types.includes("administrative_area_level_1")) {
                  state = component.long_name;
                } else if (types.includes("country")) {
                  country = component.long_name;
                } else if (types.includes("postal_code")) {
                  zipCode = component.long_name;
                } else if (
                  types.includes("street_number") ||
                  types.includes("route") ||
                  types.includes("premise")
                ) {
                  addressLine1 += component.long_name + " ";
                } else if (
                  types.includes("neighborhood") ||
                  types.includes("sublocality") ||
                  types.includes("sublocality_level_1") ||
                  types.includes("sublocality_level_2")
                ) {
                  addressLine2 += component.long_name + " ";
                }
              }

              fullAddress = [addressLine1.trim(), addressLine2.trim()]
                .filter(Boolean)
                .join(", ");

              const formattedAddress = (
                place.label ||
                fullAddress.trim() ||
                ""
              ).trim();
              setInputValue(formattedAddress);
              props.onLocationChange({
                place: placeName,
                country: country,
                address_line1: addressLine1.trim(),
                address_line2: addressLine2.trim(),
                city: city,
                state: state,
                postal_code: zipCode,
                region: "IND",
                fullAddress: formattedAddress,
              });
            } catch (error) {
              if (inputValue && inputValue.trim()) {
                handleManualAddress(inputValue.trim());
              } else {
                setInputValue("");
                props.onLocationChange({
                  place: "",
                  country: "",
                  address_line1: "",
                  address_line2: "",
                  city: "",
                  state: "",
                  postal_code: "",
                  region: "",
                });
              }
            }
          },
          noOptionsMessage: ({ inputValue }: { inputValue: string }) => {
            if (inputValue && inputValue.trim()) {
              return `Press Enter to use "${inputValue}" as your address`;
            }
            return "No options found";
          },
          onKeyDown: (event: React.KeyboardEvent) => {
            if (event.key === "Enter" && inputValue && inputValue.trim()) {
              event.preventDefault();
              handleManualAddress(inputValue.trim());
            }
          },
        }}
        autocompletionRequest={{
          componentRestrictions: {
            country: ["in"],
          },
        }}
        {...props}
      />
      <Typography
        variant="body2"
        sx={{
          fontSize: "0.75rem",
          color: "#e46a76",
          marginLeft: 2,
          marginTop: 0.75,
        }}
      >
        {props.error && props.helperText}
      </Typography>

      <IconButton
        sx={{
          position: "absolute",
          right: 0,
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 10,
        }}
        onClick={() => setMapPickerOpen(true)}
      >
        <LocationOn color="primary" />
      </IconButton>

      {mapPickerOpen && (
        <MapPicker
          open={mapPickerOpen}
          onClose={() => setMapPickerOpen(false)}
          onHandleLocationSelect={handleMapPickerLocationSelect}
        />
      )}
    </Box>
  );
};
